import { useEffect } from 'react'
import { Observer, observer } from 'mobx-react-lite'
import { Badge, Typography, Layout, Tooltip } from 'antd'
import { CiStar } from 'react-icons/ci'
import { LuMenu, LuMoon, LuSearch, LuSun, LuTriangleAlert } from 'react-icons/lu'

import { useLayoutContext } from '@/Core/Layouts/DefaultLayout'

import { Button } from '@/Core/UI/Button'
import { IoMdNotificationsOutline } from 'react-icons/io'
import { Search } from '@/Ship/Components/UI/Search'
import { useNotificationDrawer } from '@/Containers/CommunicationSection/NotificationContainer/Hooks/useNotificationDrawer'
import { useFavoritesDrawer } from '@/Containers/CommunicationSection/FavoritesContainer/Hooks/useFavoritesDrawer'
import { UserBlock } from '@/Ship/Components/UserBlock'
import { classNames } from '@/Ship/Helpers/classNames'
import { BrandConfig } from '@/Ship/Configs/BrandConfig'
import { switchTheme } from '@/Ship/Components/Theme'
import { favoritesManager } from '@/Ship/Stores/FavoritesManager'
import { layoutManager } from '@/Ship/Stores/LayoutManager'
import { themeManager } from '@/Ship/Stores/ThemeManager'

import styles from './HeaderContent.module.css'

const { Header } = Layout

/** Скільки записів в обраному — бейдж на ☆ (`SPEC-014` §4.5): усі, незалежно від пошуку в шухляді. */
const FavoritesBadge = observer(({ children }: { children: React.ReactNode }) => (
    <Badge count={favoritesManager.isLoaded ? favoritesManager.items.length : 0} size="small" overflowCount={99} offset={[-4, 4]}>
        {children}
    </Badge>
))

/**
 * Шапка оболонки (`SPEC-014` §1.2, §1.4): назва сторінки, пошук, дзвіночок,
 * «Обране» з лічильником, тема і блок користувача.
 *
 * На ≤ 670 px ліворуч з'являється ☰ (меню шухлядою), пошук ховається за 🔍 і
 * розгортається на всю ширину під шапкою, а перемикач теми переїжджає в меню
 * блоку користувача.
 */
export const HeaderContent = observer(() => {
    const layoutContext = useLayoutContext()
    const { openNotificationDrawer, NotificationBadge, NotificationDrawer } = useNotificationDrawer()
    const { openFavoritesDrawer, FavoritesDrawer } = useFavoritesDrawer()
    const { isMobile, isMobileSearchOpen } = layoutManager

    // Обране — на старті оболонки, як у легасі (`FavoriteList.jsx:62-64`): бейдж
    // на ☆ має бути правдивим ще до першого відкриття шухляди
    useEffect(() => {
        favoritesManager.ensureLoaded().catch((error: unknown) => {
            console.warn('HeaderContent: не вдалося прочитати обране', error)
        })
    }, [])

    return (
        <>
            <Header className={classNames(styles['header'], isMobile && styles['headerMobile'])}>
                {isMobile && (
                    <Button
                        aria-label="Меню"
                        icon={<LuMenu style={{ fontSize: '20px' }} strokeWidth={1.75} />}
                        className={classNames(styles['button'])}
                        onClick={() => layoutManager.setMobileMenuOpen(true)}
                    />
                )}

                <Observer>
                    {() => {
                        const layoutTitle = layoutContext?.states?.['layoutTitle'] || ''

                        return <Typography.Text className={classNames(styles['title'])}>{layoutTitle}</Typography.Text>
                    }}
                </Observer>

                <div className={classNames(styles['spacer'])}>
                    {/* Попередження демо-стенда (ADR 048, `headerNotice` профілю); на телефоні — лише «DEMO» з повним текстом у підказці */}
                    {BrandConfig.headerNotice !== undefined && (
                        <Tooltip title={isMobile ? BrandConfig.headerNotice : ''}>
                            <span
                                className={classNames(styles['demoNotice'])}
                                role="note"
                                aria-label={BrandConfig.headerNotice}
                                data-testid="header-demo-notice"
                            >
                                <LuTriangleAlert className={classNames(styles['demoNoticeIcon'])} aria-hidden />
                                <span className={classNames(styles['demoNoticeText'])}>
                                    {isMobile ? 'DEMO' : BrandConfig.headerNotice}
                                </span>
                            </span>
                        </Tooltip>
                    )}
                </div>

                <div className={classNames(styles['actions'])}>
                    {isMobile ? (
                        <Button
                            aria-label="Пошук"
                            aria-pressed={isMobileSearchOpen}
                            icon={<LuSearch style={{ fontSize: '18px' }} strokeWidth={1.75} />}
                            className={classNames(styles['button'])}
                            onClick={() => layoutManager.toggleMobileSearch()}
                        />
                    ) : (
                        <Search />
                    )}

                    <Tooltip title="Сповіщення" placement="bottom">
                        <span style={{ display: 'inline-flex' }}>
                            <NotificationBadge>
                                <Button
                                    aria-label="Сповіщення"
                                    icon={<IoMdNotificationsOutline style={{ fontSize: '20px' }} />}
                                    className={classNames(styles['button'])}
                                    onClick={openNotificationDrawer}
                                />
                            </NotificationBadge>
                        </span>
                    </Tooltip>

                    <Tooltip title="Обране" placement="bottom">
                        <span style={{ display: 'inline-flex' }}>
                            <FavoritesBadge>
                                <Button
                                    aria-label="Обране"
                                    icon={<CiStar style={{ fontSize: '20px' }} />}
                                    className={classNames(styles['button'])}
                                    onClick={openFavoritesDrawer}
                                />
                            </FavoritesBadge>
                        </span>
                    </Tooltip>

                    {/* Місяць і сонце — з Lucide, а не з Circum, як сусіди: сонце Circum
                        на цьому розмірі читається як шестерня налаштувань. На мобільному
                        перемикач живе в меню блоку користувача (`SPEC-014` §1.4) */}
                    {!isMobile && (
                        <Tooltip title={themeManager.isDark ? 'Світла тема' : 'Темна тема'} placement="bottom">
                            <span style={{ display: 'inline-flex' }}>
                                <Button
                                    aria-label={themeManager.isDark ? 'Світла тема' : 'Темна тема'}
                                    icon={
                                        themeManager.isDark ? (
                                            <LuSun className="wl-theme-icon" style={{ fontSize: '18px' }} strokeWidth={1.5} />
                                        ) : (
                                            <LuMoon className="wl-theme-icon" style={{ fontSize: '18px' }} strokeWidth={1.5} />
                                        )
                                    }
                                    className={classNames(styles['button'])}
                                    // Коло нової теми розходиться від самої кнопки
                                    onClick={(event) => switchTheme(event.currentTarget)}
                                />
                            </span>
                        </Tooltip>
                    )}

                    {/* Блок користувача замінює кнопки PD «Профіль» і «Вихід» (`SPEC-014` §1.2) */}
                    <UserBlock />
                </div>
            </Header>

            {isMobile && isMobileSearchOpen && (
                <div className={classNames(styles['mobileSearch'])}>
                    <Search block />
                </div>
            )}

            <NotificationDrawer />
            <FavoritesDrawer />
        </>
    )
})

HeaderContent.displayName = 'HeaderContent'
